import React, { useState } from 'react';
import { AlertCircle, X, Github, GitlabIcon } from 'lucide-react';

interface ReauthConnection {
  connectionId: string;
  provider: string;
  username: string;
  missingScopes: string[];
  reason: string;
}

interface ReauthBannerProps {
  connections: ReauthConnection[];
  onReauthorize: (provider: string, connectionId: string) => void;
  onDismiss?: () => void;
}

export const ReauthBanner: React.FC<ReauthBannerProps> = ({
  connections,
  onReauthorize,
  onDismiss,
}) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || connections.length === 0) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const getProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'github':
        return <Github className="w-5 h-5" />;
      case 'gitlab':
        return <GitlabIcon className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  return (
    <div className="mb-6">
      {connections.map((connection) => (
        <div
          key={connection.connectionId}
          className="bg-white dark:bg-gray-800 rounded-xl border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 mb-3"
        >
          <div className="flex items-start gap-4">
            {/* Provider Icon */}
            <div className="shrink-0 p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
              {getProviderIcon(connection.provider)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3
                    className="text-sm font-semibold mb-1"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    Reauthorization Required
                  </h3>
                  <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    <span className="font-medium capitalize">{connection.provider}</span> connection
                    for <span className="font-medium">{connection.username}</span>
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">{connection.reason}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => onReauthorize(connection.provider, connection.connectionId)}
                    className="btn-primary text-sm px-4 py-2 whitespace-nowrap"
                  >
                    Reauthorize
                  </button>
                  {onDismiss && (
                    <button
                      onClick={handleDismiss}
                      className="p-2 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                      aria-label="Dismiss"
                    >
                      <X className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
